<template>
  <v-container fluid>
    <h2>
      {{ $tc('report', 2)}}
      <v-dialog v-model="createDialog" width="66%">
        <report-create-form
          ref="create-form"
          :report-options="reportOptions"
          @close="createDialog = false"
          @cloneReport="cloneReport($event)"/>
      </v-dialog>
    </h2>
    <div>
      <div class="text-subtitle-1 font-weight-bold mb-2">
        {{ $t('create_a_new_report') }}
      </div>
      <v-sheet color="grey lighten-5">
        <v-card-text>
          <v-row dense>
            <v-col v-for="report in reportOptions" :key="report.type" cols="12" md="4" lg="3" xl="2">
              <v-card
                outlined
                class="d-flex align-center"
                height="70"
                @click="createReport(report.type)">
                <v-icon class="ml-2 report-icon" style="width: 50px">
                  {{ report.formIcon }}
                </v-icon>
                {{ report.name }}
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-sheet>
    </div>
    <v-divider class="my-6"/>
    <report-index></report-index>
  </v-container>
</template>

<script>
import ReportCreateForm from '@/components/report/ReportCreateForm'
import ReportIndex from '@/components/report/ReportIndex'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { filter, some } from 'lodash'

export default {
  components: {
    ReportCreateForm,
    ReportIndex
  },
  mixins: [
    CurrentUserMixin
  ],
  data () {
    return {
      createDialog: false,
      selectedReport: null,
      // The master list of report types that we can create.
      reportTypes: [
        {
          type: 'SiteViolationReport',
          name: this.$t('reports_ui.site_violation_report.name'),
          form: 'SiteViolationReportForm',
          infoText: this.$t('reports_ui.site_violation_report.info_text'),
          exampleImage: '/img/reports/example-site-violations.png',
          formIcon: 'mdi-store-alert'
        },
        {
          type: 'MonthlyRouteStatusReport',
          name: this.$t('reports_ui.monthly_route_status_report.name'),
          form: 'MonthlyRouteStatusReportForm',
          infoText: this.$t('reports_ui.monthly_route_status_report.info_text'),
          exampleImage: '/img/reports/example-monthly-route-status-report.png',
          formIcon: 'mdi-road'
        },
        {
          type: 'VehicleViolationReport',
          name: this.$t('reports_ui.vehicle_violation_report.name'),
          form: 'vehicleViolationReportForm',
          infoText: this.$t('reports_ui.vehicle_violation_report.info_text'),
          exampleImage: '/img/reports/example-vehicle-violations.png',
          formIcon: 'mdi-truck-alert'
        },
        {
          type: 'RootCauseReport',
          showReport: () => {
            return this.customerContext.getSetting('enableViolationRootCauses') === 1 && this.user.can('view_routeassessment')
          },
          name: this.$t('reports_ui.root_cause_report.name'),
          form: 'RootCauseReportForm',
          infoText: this.$t('reports_ui.root_cause_report.info_text'),
          exampleImage: '/img/reports/example-violation-root-cause.png',
          formIcon: 'mdi-scale-balance'
        },
        {
          type: 'MissedStopsReport',
          name: this.$t('reports_ui.missed_stops_report.name'),
          form: 'MissedStopsReportForm',
          infoText: this.$t('reports_ui.missed_stops_report.info_text'),
          exampleImage: '/img/reports/example-missed-stops.png',
          formIcon: 'mdi-map-marker-alert'
        },
        {
          type: 'StartTemperaturesReport',
          name: this.$t('reports_ui.start_temperatures_report.name'),
          form: 'StartTemperaturesReportForm',
          infoText: this.$t('reports_ui.start_temperatures_report.info_text'),
          exampleImage: '/img/reports/example-start-temperatures.png',
          formIcon: 'mdi-thermometer'
        },
        {
          type: 'FleetHealthReport',
          showReport: () => {
            return this.customerContext.getSetting('fleetHealth', {}).enabled === 1
          },
          name: this.$t('reports_ui.fleet_health_report.name'),
          form: 'FleetHealthReportForm',
          infoText: this.$t('reports_ui.fleet_health_report.info_text'),
          exampleImage: '/img/reports/example-fleet-health-report.png',
          formIcon: 'mdi-lightbulb'
        },
        {
          type: 'RouteAssessmentMetricsReport',
          name: this.$t('reports_ui.route_assessment_metrics_report.name'),
          form: 'RouteAssessmentMetricsReportForm',
          infoText: this.$t('reports_ui.route_assessment_metrics_report.info_text'),
          exampleImage: '/img/reports/example-route-assessment-metrics.png',
          formIcon: 'mdi-clipboard-list'
        },
        {
          type: 'WeeklyPerformanceReport',
          name: this.$t('reports_ui.weekly_performance_report.name'),
          form: 'WeeklyPerformanceReportForm',
          infoText: this.$t('reports_ui.weekly_performance_report.info_text'),
          exampleImage: '/img/reports/example-weekly-performance.png',
          formIcon: 'mdi-clipboard-list'
        },
        /* Hide weekly scorecard report per VF-3224
        {
          type: 'WeeklyScorecardReport',
          name: this.$t('reports_ui.weekly_scorecard_report.name'),
          form: 'WeeklyScorecardReportForm',
          infoText: this.$t('reports_ui.weekly_scorecard_report.info_text'),
          exampleImage: '/img/reports/example-weekly-scorecard.png',
          formIcon: 'mdi-clipboard-list'
        },
        */
        {
          type: 'CustomerMetricsReport',
          showReport: () => {
            return this.user.can('view_rootentity')
          },
          variant: 'root-only',
          name: this.$t('reports_ui.customer_metrics_report.name'),
          form: 'CustomerMetricsReportForm',
          infoText: this.$t('reports_ui.customer_metrics_report.info_text'),
          exampleImage: '/img/reports/example-customer-metrics.png',
          formIcon: 'mdi-office-building'
        },
        {
          type: 'RouteDataQualityReport',
          showReport: () => {
            return this.user.can('view_rootentity')
          },
          variant: 'root-only',
          name: this.$t('reports_ui.route_data_quality_report.name'),
          form: 'RouteDataQualityReportForm',
          infoText: this.$t('reports_ui.route_data_quality_report.info_text'),
          exampleImage: '/img/reports/example-route-data-quality.png',
          formIcon: 'mdi-clipboard-list'
        },
        {
          type: 'RCAReport',
          name: this.$t('reports_ui.rca_report.name'),
          form: 'RCAReportForm',
          infoText: this.$t('reports_ui.rca_report.info_text'),
          exampleImage: '/img/reports/example-rca.png',
          formIcon: 'mdi-clipboard-list'
        },
        {
          type: 'StartTempLogReport',
          name: this.$t('reports_ui.start_temp_log_report.name'),
          form: 'StartTempLogReportForm',
          infoText: this.$t('reports_ui.start_temp_log_report.info_text'),
          exampleImage: '/img/reports/example-start-temp-log.png',
          formIcon: 'mdi-clipboard-list'
        },
        {
          variant: 'root-only',
          showReport: () => {
            return this.user.can('view_rootentity')
          },
          type: 'VehicleCountReport',
          name: this.$t('reports_ui.vehicle_count_report.name'),
          form: 'VehicleCountReportForm',
          infoText: this.$t('reports_ui.vehicle_count_report.info_text'),
          exampleImage: '/img/reports/example-vehicle-count.png',
          formIcon: 'mdi-car'
        },
        {
          type: 'ForecastViolationValidationReport',
          showReport: () => {
            return this.user.can('view_rootentity')
          },
          variant: 'root-only',
          name: 'Forecast Violation Validation Report',
          form: 'ForecastViolationValidationReportForm',
          infoText: 'Analyze the accuracy and performance of forecast violation predictions with detailed metrics including accuracy, precision, recall, F1 score, and breakdowns by violation type, zone, and forecast phase.',
          exampleImage: '/img/reports/example-site-violations.png',
          formIcon: 'mdi-clipboard-check'
        }
      ]
    }
  },
  computed: {
    /**
     * Report options filtered to what the user can see
     * @returns {Array}
     */
    reportOptions () {
      return filter(this.reportTypes, report => report.showReport == null || report.showReport())
    }
  },
  methods: {
    createReport (type) {
      this.createDialog = true
      this.$nextTick(() => {
        this.$refs['create-form'].setReportType(type)
      })
    },
    /**
     * Clone a report. Basically just opens the 'new report' dialog and prepopulates it with the selected report's
     * parameters.
     * @param report
     */
    cloneReport (report) {
      this.createDialog = true
      this.$nextTick(() => {
        this.$refs['create-form'].preFillForm(report)
      })
    }
  }
}
</script>
